/**
 * Community detail screen (root-stack 'Community', param { name }).
 *
 * MIGRATION (bare RN, P3 COMMUNITIES group): ported off apps/cryb-native
 * app/community/[name].tsx. Swaps: expo-router → react-navigation
 * (useLocalSearchParams → route.params via useRoute; useRouter → useNavigation;
 * inline <Stack.Screen options> → navigation.setOptions); @expo/vector-icons →
 * Icon shim; expo-linear-gradient → react-native-linear-gradient. The LR5 live
 * member-count realtime room join/leave + member_update handler are preserved
 * verbatim. The A15 report/block sheet is preserved.
 *
 * Renders a single community's banner/info header plus four tabbed sections
 * (feed, members, about, leaderboard). Tab data is lazily fetched the first time
 * a tab is shown after the community itself loads.
 *
 * API endpoints:
 *   GET    /communities/:name                 — community metadata
 *   GET    /communities/:name/announcements   — pinned announcements
 *   GET    /communities/:name/posts           — feed tab
 *   GET    /communities/:name/members         — members tab
 *   GET    /communities/:name/leaderboard     — leaderboard tab
 *   POST   /communities/:name/join            — join
 *   DELETE /communities/:name/join            — leave
 */
import { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  Pressable,
  Modal,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TextInput,
  useWindowDimensions,
  Alert,
  Linking,
  Share,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import { launchImageLibrary } from 'react-native-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, IoniconName } from '../components/ui/Icon';
import { api, uploadMedia, getLastUploadError } from '../services/api';
import { communityIcon, communityCover } from '../lib/icons';
import type { RootStackParamList } from '../navigation/types';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale, shadowTokens,
} from '../constants/theme';
import { GlassCard, GlassButton, GlassSurface } from '../components/glass';
import { SOLIDS as CARD_SOLIDS } from '../components/discover/CoinCommunityCard';

// react-native-video is loaded lazily (already a project dep — StoryViewer uses
// the same pattern) so a community with a VIDEO picture/banner plays inline while
// the screen stays tsc-clean and crash-free if the module is ever absent.
let RNVideo: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  RNVideo = require('react-native-video').default ?? require('react-native-video');
} catch {
  RNVideo = null;
}

// A CRYB-set community media value can be one of three kinds:
//   • an http(s) IMAGE url  • an http(s) VIDEO url  • a solid 'color:#RRGGBB'
// token (device-local until a server color column exists). These helpers keep the
// banner + avatar renderers honest about which one they're showing.
const COLOR_PREFIX = 'color:';
function isColorToken(v?: string | null): v is string {
  return typeof v === 'string' && v.startsWith(COLOR_PREFIX);
}
function colorFromToken(v?: string | null): string | null {
  return isColorToken(v) ? v.slice(COLOR_PREFIX.length) : null;
}
function isVideoUrl(v?: string | null): boolean {
  if (!v || typeof v !== 'string' || v.startsWith(COLOR_PREFIX)) return false;
  return /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(v);
}
import ParticleBackground from '../components/ui/ParticleBackground';
import LoadingState from '../components/ui/LoadingState';
import ProgressRow from '../components/ui/ProgressRow';
import {
  SegmentedTabs, OverflowMenu, PinnedChannelTile, ChannelGridTile,
  parseGroupedSections, deriveSections, pickPinned, isVoice,
} from '../components/community';
import type { OverflowAction, Channel, ChannelSection, ChannelTileData } from '../components/community';
import { AvatarStack } from '../components/discover';
import ReportBlockSheet, { ReportTarget } from '../components/moderation/ReportBlockSheet';
import { isBlocked, onBlockedChange, isMuted, onMutedChange } from '../services/moderation';
import { joinCommunityRoom, leaveCommunityRoom, onUserEvent } from '../services/socket';

type Nav = NativeStackNavigationProp<RootStackParamList, 'Community'>;
type CommunityRoute = RouteProp<RootStackParamList, 'Community'>;

interface CommunityAnnouncement {
  id: string;
  title: string;
  content?: string;
  link?: string | null;
  type?: string;
  createdAt?: string;
}

type TabKey = 'channels' | 'feed' | 'members' | 'about' | 'leaderboard';

interface Community {
  name: string;
  displayName: string;
  description: string;
  banner: string;
  avatar: string;
  memberCount: number;
  joined?: boolean;
  rules?: string[];
  createdAt: string;
}

interface PostItem {
  id: string;
  title?: string;
  content: string;
  author: { id?: string; username: string; displayName: string; avatar: string };
  createdAt: string;
  likes: number;
  comments: number;
}

// The backend returns community posts with `User` (not author), `upvotes`/`score`
// (not likes), and `commentCount`/`_count.Comment` (not comments). Without this
// map the feed showed "Unknown / @unknown / 0 / 0" for every post.
function normalizeCommunityPost(raw: any): PostItem {
  const u = raw?.User ?? {};
  return {
    id: raw?.id,
    title: raw?.title ?? '',
    content: raw?.content ?? '',
    author: {
      id: u.id ?? raw?.userId ?? undefined,
      username: u.username ?? '',
      displayName: u.displayName ?? u.username ?? 'Unknown',
      avatar: u.avatar ?? '',
    },
    createdAt: raw?.createdAt ?? '',
    likes: raw?.upvotes ?? raw?.score ?? 0,
    comments: raw?._count?.Comment ?? raw?.commentCount ?? 0,
  };
}

// GET /communities/:name/members returns { data: { items: [{ id, userId, karma,
// isModerator, User: { id, username, displayName, avatar } }] } }. The identity
// lives under `User`; the row maps it via memberIdentity() below.
interface Member {
  id?: string;
  userId?: string;
  isModerator?: boolean;
  User?: {
    id?: string;
    username?: string;
    displayName?: string;
    avatar?: string;
  };
}

// Pull a member's renderable identity out of the nested User include, with
// safe fallbacks so a malformed row never crashes the grid.
function memberIdentity(m: Member): { username: string; displayName: string; avatar: string; role?: string } {
  const u = m?.User ?? {};
  return {
    username: u.username ?? '',
    displayName: u.displayName ?? u.username ?? '',
    avatar: u.avatar ?? '',
    role: m?.isModerator ? 'mod' : undefined,
  };
}

interface LeaderboardEntry {
  rank: number;
  username: string;
  displayName: string;
  avatar: string;
  points: number;
}

const TABS: { key: TabKey; label: string }[] = [
  { key: 'channels', label: 'Channels' },
  { key: 'feed', label: 'Feed' },
  { key: 'members', label: 'Members' },
  { key: 'about', label: 'About' },
  { key: 'leaderboard', label: 'Leaderboard' },
];

// Same brand palette + hash as the communities list, so a community's detail
// header matches the color of its card.
const COMMUNITY_GRADIENTS: [string, string][] = [
  ['#F7931A', '#FFB347'], ['#6C5CE7', '#a855f7'], ['#16A34A', '#22C55E'],
  ['#2563EB', '#3B82F6'], ['#DC2626', '#F87171'], ['#0D9488', '#2DD4BF'],
  ['#1E293B', '#475569'], ['#CA8A04', '#FACC15'], ['#DB2777', '#F472B6'],
  ['#7C3AED', '#6366F1'],
];
function gradientFor(key: string): [string, string] {
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return COMMUNITY_GRADIENTS[h % COMMUNITY_GRADIENTS.length];
}

// Plural-aware count label: pluralize(1,'member') → "1 member"; (2) → "2 members".
function pluralize(n: number, singular: string, plural?: string): string {
  const safe = Number.isFinite(n) ? n : 0;
  const word = safe === 1 ? singular : (plural ?? `${singular}s`);
  return `${safe.toLocaleString()} ${word}`;
}

export default function CommunityScreen() {
  const route = useRoute<CommunityRoute>();
  const { name } = route.params ?? ({} as { name: string });
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  // Constrain content width on tablet/desktop so it doesn't stretch edge-to-edge.
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  // Members grid scales with width: 3 up on phone, 4 on tablet, 5 on desktop.
  const memberColumns = layout === 'desktop' ? 5 : layout === 'tablet' ? 4 : 3;

  // --- State ---
  const [community, setCommunity] = useState<Community | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('channels');
  const [loading, setLoading] = useState(true);
  // Fetch outcome for the community itself: null = ok / not-yet, 'offline' = network
  // or 5xx (retryable), 'notfound' = genuine 404. Lets us distinguish a transient
  // failure (Retry) from a real missing community (Go back) instead of always
  // rendering "Community not found" (mirrors ServerDetail's LoadState branching).
  const [fetchError, setFetchError] = useState<'offline' | 'notfound' | null>(null);
  const [posts, setPosts] = useState<PostItem[]>([]);
  // Per-tab load flag + error (feed/members/leaderboard). Without these the empty
  // state flashed instantly and a silent failure stuck as a false "nothing yet".
  const [tabLoading, setTabLoading] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);
  // In-flight guard for join/leave — disables the CTA while pending so a double-tap
  // can't fire two toggles (the race that flipped the count twice).
  const [joining, setJoining] = useState(false);
  // Token-gating lock: set when GET /:name/posts returns 403 code TOKEN_GATED
  // (this community requires holding specific tokens/NFTs). Renders a lock screen
  // on the feed tab instead of the post list.
  const [gated, setGated] = useState<{ reason: string } | null>(null);
  // Trust & Safety (Apple 1.2): report/block sheet target + re-render on block.
  const [tsTarget, setTsTarget] = useState<ReportTarget | null>(null);
  const [, setBlockTick] = useState(0);
  useEffect(() => {
    const bump = () => setBlockTick((n) => n + 1);
    const unsubBlocked = onBlockedChange(bump);
    const unsubMuted = onMutedChange(bump);
    return () => { unsubBlocked(); unsubMuted(); };
  }, []);
  const [members, setMembers] = useState<Member[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [announcements, setAnnouncements] = useState<CommunityAnnouncement[]>([]);
  // Channels-model interior (uniform with ServerDetail): grouped channel sections.
  const [sections, setSections] = useState<ChannelSection[]>([]);

  // "…" overflow menu (headerRight) — Share + Leave only (both real; there is
  // no community-report endpoint, so Report is intentionally absent).
  const [menuOpen, setMenuOpen] = useState(false);

  // Owner/admin gate for the Edit affordance. GET /communities/:name/members/me
  // returns { role, isModerator } — a community's creator is seeded as a
  // Moderator with { all: true }, so isModerator === owner/admin here. Only they
  // may PATCH the picture/banner (server enforces the same on PATCH /:name).
  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);
  // Device-local card color set at creation time (CreateCommunity persists
  // `communityColor:<slug>`), used as the solid fallback when no image/video.
  const [localColor, setLocalColor] = useState<string | null>(null);
  // Edit sheet: which surface is being changed + its transient state.
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<'avatar' | 'banner'>('banner');
  // Pending, un-saved value while the sheet is open (a media uri, a color token,
  // or null=keep current). Saved via PATCH on "Save".
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Edit name & description sheet (owner/admin) → PATCH /communities/:name.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsName, setDetailsName] = useState('');
  const [detailsDesc, setDetailsDesc] = useState('');
  const [detailsRules, setDetailsRules] = useState<string[]>([]);
  const [savingDetails, setSavingDetails] = useState(false);
  // Announcement editor (owner/admin): create or edit. `annEditId` null = create.
  const [annOpen, setAnnOpen] = useState(false);
  const [annEditId, setAnnEditId] = useState<string | null>(null);
  const [annTitle, setAnnTitle] = useState('');
  const [annContent, setAnnContent] = useState('');
  const [savingAnn, setSavingAnn] = useState(false);
  // Banned-members manager (owner/admin) → GET/DELETE /communities/:name/bans.
  const [bansOpen, setBansOpen] = useState(false);
  const [bans, setBans] = useState<Array<{ id: string; reason?: string | null; user: { username: string; displayName?: string; avatar?: string } | null }>>([]);
  const [loadingBans, setLoadingBans] = useState(false);

  // Self-manage the top bar (Apple skill §12): the native stack header is fully
  // suppressed so the aurora ParticleBackground + gradient banner bleed full-bleed
  // under the status bar with no opaque top strip. In-screen translucent floating
  // controls (back chevron + overflow) ride over the banner instead — content
  // scrolls under them. This also removes the stale "‹ Tabs" route-name back label.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // --- Effects ---
  // Reload community + announcements whenever the route param changes.
  useEffect(() => {
    fetchCommunity();
    fetchAnnouncements();
    fetchMyRole();
    // Read back the device-local card color chosen at creation (best-effort).
    (async () => {
      try {
        const c = await AsyncStorage.getItem(`communityColor:${String(name || '')}`);
        setLocalColor(c && c.length > 0 ? c : null);
      } catch { setLocalColor(null); }
    })();
  }, [name]);

  // LR5: live member count. Join the community's realtime room (by name — the
  // server resolves name→id and joins both `community:<id>`/`community:<name>`
  // rooms) and update the count when any user joins/leaves. The REST join/leave
  // routes broadcast `community:member_update` { communityName, memberCount }.
  // Matches on communityName so we don't need the (unstored) community id.
  useEffect(() => {
    const cname = String(name || '');
    if (!cname) return;
    joinCommunityRoom(cname);
    const unsub = onUserEvent('community:member_update', (payload: any) => {
      if (!payload) return;
      const matches = payload.communityName === cname || payload.communityId === cname;
      if (!matches) return;
      const count = Number(payload.memberCount);
      if (!Number.isFinite(count)) return;
      setCommunity((prev) => (prev ? { ...prev, memberCount: count } : prev));
    });
    return () => {
      unsub();
      leaveCommunityRoom(cname);
    };
  }, [name]);

  // Lazily fetch the active tab's data. Gated on `community` so tab fetches only
  // fire once the community has resolved (and re-run when switching tabs). Reset
  // the per-tab error on every switch so a stale error from another tab never
  // bleeds through; each list-backed fetch owns its own loading flag.
  // Depend on the stable `community?.name` key — NOT the whole `community` object,
  // which is a fresh reference after every optimistic join/leave/member-count
  // toggle and would needlessly refetch the active tab (posts/members) + flicker.
  useEffect(() => {
    if (!community) return;
    setTabError(null);
    if (activeTab === 'channels') fetchChannels();
    if (activeTab === 'feed') fetchPosts();
    if (activeTab === 'members') fetchMembers();
    if (activeTab === 'leaderboard') fetchLeaderboard();
  }, [activeTab, community?.name]);

  // --- Data fetching ---
  async function fetchCommunity() {
    setLoading(true);
    // GET /communities/:name returns `icon` (not `avatar`) and has NO membership
    // flag. Map avatar←icon; read joined from isJoined when the payload carries
    // it (the LIST endpoint does), else keep any existing flag, else false.
    try {
      const res = await api<any>(`/communities/${name}`);
      if (res.success && res.data) {
        // Device-local `color:` overrides (set by the owner in the Edit sheet
        // since the backend has no color column) win over the server value so a
        // solid-color banner/avatar survives reloads until server sync lands.
        let avatarOverride: string | null = null;
        let bannerOverride: string | null = null;
        try {
          [avatarOverride, bannerOverride] = await Promise.all([
            AsyncStorage.getItem(`communityMedia:${name}:avatar`),
            AsyncStorage.getItem(`communityMedia:${name}:banner`),
          ]);
        } catch { /* best-effort */ }
        setFetchError(null);
        setCommunity({
          ...res.data,
          avatar: avatarOverride ?? res.data.icon ?? res.data.avatar,
          banner: bannerOverride ?? res.data.banner,
          joined: res.data.isJoined ?? res.data.joined ?? false,
        });
      } else {
        // Distinguish a genuine 404 (missing community → "not found", Go back) from
        // a transient failure (network/5xx → offline, Retry). api() hides the HTTP
        // status, so classify by code/message; anything not clearly 404 is treated
        // as retryable so a transient blip never renders a permanent "not found".
        const err = res.error || '';
        const isNotFound = res.code === 'NOT_FOUND' || /\b404\b/.test(err) || /not found/i.test(err);
        setFetchError(isNotFound ? 'notfound' : 'offline');
      }
    } catch {
      // Network error — retryable offline state (never "not found").
      setFetchError('offline');
    } finally {
      // Guaranteed so the screen can never get stuck on the spinner.
      setLoading(false);
    }
  }

  // Resolve whether the signed-in user is the owner/admin (moderator) of this
  // community. 404 = not a member → not an admin. Never throws; a failure just
  // leaves the Edit affordance hidden (fail-closed, correct for a permission).
  async function fetchMyRole() {
    try {
      const res = await api<any>(`/communities/${name}/members/me`);
      setIsOwnerAdmin(!!(res.success && res.data?.isModerator));
    } catch {
      setIsOwnerAdmin(false);
    }
  }

  async function fetchAnnouncements() {
    try {
      // Backend sends { success, announcements: [...] } with NO data wrapper, so
      // api() unwraps to the whole body — the array is at res.data.announcements.
      const res = await api<{ announcements: CommunityAnnouncement[] }>(`/communities/${name}/announcements`);
      const list = res.success && Array.isArray(res.data?.announcements) ? res.data!.announcements : [];
      // Drop malformed entries — an announcement without a title is unrenderable.
      setAnnouncements(list.filter((a) => a && typeof a.title === 'string' && a.title.length > 0));
    } catch {
      setAnnouncements([]);
    }
  }

  // Announcement links may be external (http/https) or internal app routes (/...).
  // External links open in the system browser; internal links are handled by the
  // app's deep-link config when present. Anything else (or a bad URL) is ignored.
  async function handleAnnouncementLink(link?: string | null) {
    if (!link || typeof link !== 'string') return;
    try {
      if (/^https?:\/\//i.test(link)) {
        await Linking.openURL(link);
      } else if (link.startsWith('/')) {
        // Route through the linking layer so cryb:// deep links resolve.
        await Linking.openURL(`cryb:/${link.replace(/^\/+/, '/')}`);
      }
    } catch {
      // swallow — never crash on a bad link
    }
  }

  // Channels-model: fetch grouped sections; fall back to client-side derivation
  // from the flat channel list so the tab works even against the legacy shape.
  // Never throws (an error just leaves the "No channels yet" empty state).
  async function fetchChannels() {
    try {
      const res = await api<any>(`/communities/${name}/channels?grouped=true`);
      if (res.success) {
        const grouped = parseGroupedSections(res.data);
        if (grouped) { setSections(grouped); return; }
        const flat = Array.isArray(res.data?.channels) ? res.data.channels : null;
        if (flat) { setSections(deriveSections(flat as Channel[])); return; }
      }
      setSections([]);
    } catch {
      setSections([]);
    }
  }

  // Open a channel from a tile (voice → VoiceRoom, else the Channel chat screen).
  function openChannel(c: ChannelTileData) {
    if (isVoice(c)) {
      navigation.navigate('VoiceRoom', { id: c.id });
    } else {
      navigation.navigate('Channel', { id: c.id, name: c.name });
    }
  }

  // Owner/admin: create a channel (Alert.prompt is iOS; the app is iOS-only).
  function addChannel() {
    (Alert as any).prompt?.(
      'New channel',
      'Name your channel (e.g. general, alpha, off-topic).',
      async (raw?: string) => {
        const text = (raw || '').trim();
        if (!text) return;
        const res = await api(`/communities/${name}/channels`, { method: 'POST', body: JSON.stringify({ name: text }) });
        if (res.success) fetchChannels();
        else Alert.alert('Error', (res as any).error || 'Could not create the channel.');
      },
      'plain-text',
    );
  }
  // Owner/admin: long-press a channel → delete.
  function confirmDeleteChannel(c: ChannelTileData) {
    Alert.alert('Delete channel', `Delete #${c.name}? This removes the channel and its messages.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          const res = await api(`/communities/${name}/channels/${c.id}`, { method: 'DELETE' });
          if (res.success) fetchChannels();
          else Alert.alert('Error', (res as any).error || 'Could not delete the channel.');
        },
      },
    ]);
  }

  async function fetchPosts() {
    setTabLoading(true);
    setTabError(null);
    try {
      // Backend wraps as { data: { posts: [...] } }.
      const res = await api<{ posts: any[] }>(`/communities/${name}/posts`);
      // Token-gated: the caller doesn't meet this community's token requirements.
      // Show the lock screen (with the server's human-readable reason) instead.
      if ((res as any).code === 'TOKEN_GATED') {
        setGated({ reason: res.error || "This community requires specific tokens to enter." });
        setPosts([]);
        return;
      }
      setGated(null);
      if (res.success && res.data) {
        const arr = Array.isArray(res.data.posts) ? res.data.posts
          : Array.isArray((res.data as any)?.items) ? (res.data as any).items
          : Array.isArray(res.data) ? (res.data as any) : [];
        setPosts(arr.map(normalizeCommunityPost));
      } else if (!res.silent) {
        // Real failure — surface a retryable error instead of a false "No posts yet"
        // (silent = auth-expiry sentinel; the auth gate handles routing).
        setTabError(res.error || 'Could not load posts.');
      }
    } finally {
      setTabLoading(false);
    }
  }

  async function fetchMembers() {
    setTabLoading(true);
    setTabError(null);
    try {
      // Backend wraps as { data: { items: [...] } }.
      const res = await api<{ items: Member[] }>(`/communities/${name}/members`);
      if (res.success && res.data) setMembers(Array.isArray(res.data.items) ? res.data.items : []);
      else if (!res.silent) setTabError(res.error || 'Could not load members.');
    } finally {
      setTabLoading(false);
    }
  }

  async function fetchLeaderboard() {
    setTabLoading(true);
    setTabError(null);
    try {
      const res = await api<LeaderboardEntry[]>(`/communities/${name}/leaderboard`);
      if (res.success && res.data) setLeaderboard(Array.isArray(res.data) ? res.data : (Array.isArray((res.data as any)?.items) ? (res.data as any).items : []));
      else if (!res.silent) setTabError(res.error || 'Could not load the leaderboard.');
    } finally {
      setTabLoading(false);
    }
  }

  // --- Handlers ---
  // Optimistic join/leave: flip the membership flag and nudge the member count
  // before the network call so the button reacts instantly. DELETE = leave,
  // POST = join. On failure we restore the prior flag + count LOCALLY (no full
  // refetch — fetchCommunity() could null the screen on a transient blip) and
  // alert the user (mirrors ServerDetail's join failure feedback). A `joining`
  // guard blocks the double-tap race.
  async function handleJoin() {
    if (!community || joining) return;
    const wasJoined = community.joined;
    setJoining(true);
    // Functional updater so a concurrent live member_update (socket) isn't clobbered.
    setCommunity((prev) => prev ? {
      ...prev,
      joined: !wasJoined,
      memberCount: prev.memberCount + (wasJoined ? -1 : 1),
    } : prev);
    // Local rollback that only touches the two fields we optimistically changed,
    // so a live socket count update in between isn't clobbered by a stale snapshot.
    const rollback = () => setCommunity((prev) => prev ? {
      ...prev,
      joined: wasJoined,
      memberCount: prev.memberCount + (wasJoined ? 1 : -1),
    } : prev);
    try {
      const res = await api(`/communities/${name}/join`, { method: wasJoined ? 'DELETE' : 'POST' });
      if (!res.success) {
        rollback();
        if (!res.silent) {
          Alert.alert(
            wasJoined ? 'Could not leave' : 'Could not join',
            res.error || 'Please try again.',
          );
        }
      }
    } catch {
      rollback();
      Alert.alert(wasJoined ? 'Could not leave' : 'Could not join', 'Please try again.');
    } finally {
      setJoining(false);
    }
  }

  // Share via the native sheet with the registered deep link (linking.ts:
  // Community ↔ 'community/:name'). iOS's share sheet includes Copy, which
  // covers copy-link without a clipboard dependency.
  async function handleShare() {
    if (!community) return;
    // Web URL (not cryb://) so the link unfurls with an OG preview and opens for
    // recipients without the app (#63). The share page deep-links into CRYB.
    const url = `https://platform.cryb.ai/c/${encodeURIComponent(community.name)}`;
    try {
      await Share.share({
        message: `Join ${community.displayName || community.name} on CRYB — ${url}`,
        url,
      });
    } catch { /* user dismissed the sheet */ }
  }

  // --- Owner/admin edit: picture + banner (image / video / solid color) ---
  // Open the edit sheet for a given surface, seeded from its current value so
  // the sheet reflects reality (a color-token current value pre-selects it).
  function openEdit(target: 'avatar' | 'banner') {
    setEditTarget(target);
    const current = target === 'avatar' ? community?.avatar : community?.banner;
    setPendingValue(current && current.length > 0 ? current : null);
    setEditOpen(true);
  }

  // Pick an image OR video from the library (mediaType 'mixed'). Local preview
  // only — the real upload is deferred to Save so a cancelled edit uploads
  // nothing (same pipeline as CreateCommunity / EditProfile).
  async function pickEditMedia() {
    try {
      const result = await launchImageLibrary({
        mediaType: 'mixed',
        selectionLimit: 1,
        quality: 0.9,
        includeBase64: false,
      });
      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Could not open the library. Please try again.');
        return;
      }
      const uri = result.assets?.[0]?.uri;
      if (uri) setPendingValue(uri);
    } catch {
      Alert.alert('Error', 'Could not open the library. Please try again.');
    }
  }

  // Commit the pending edit. A color token persists device-local (no server
  // color column yet — honest, no fake upload); a picked media uri is uploaded
  // first, then PATCH /communities/:name sets icon|banner to the returned URL.
  // A failed upload ABORTS so we never store a broken/half-applied value.
  async function saveEdit() {
    if (!community || pendingValue == null) { setEditOpen(false); return; }
    setSavingEdit(true);
    try {
      // Case 1: solid color — store as a device-local `color:` token (banner &
      // avatar render it directly). The backend has no color column; PATCH would
      // reject a non-URL, so we do NOT pretend to save it server-side.
      if (isColorToken(pendingValue)) {
        try {
          await AsyncStorage.setItem(`communityMedia:${name}:${editTarget}`, pendingValue);
        } catch { /* device-local persistence is best-effort */ }
        setCommunity((prev) => prev ? {
          ...prev,
          ...(editTarget === 'avatar' ? { avatar: pendingValue } : { banner: pendingValue }),
        } : prev);
        setEditOpen(false);
        return;
      }

      // Case 2: an already-saved remote URL left unchanged — nothing to do.
      if (/^https?:\/\//i.test(pendingValue) &&
          pendingValue === (editTarget === 'avatar' ? community.avatar : community.banner)) {
        setEditOpen(false);
        return;
      }

      // Case 3: a freshly-picked local media uri (image or video) — upload, then
      // PATCH. The server field is `icon` for the avatar and `banner` for the
      // banner; both accept an http(s) URL (image or video). Abort on failure.
      let url: string = pendingValue;
      if (!/^https?:\/\//i.test(pendingValue)) {
        const uploaded = await uploadMedia(pendingValue);
        if (!uploaded) {
          Alert.alert('Upload Failed', getLastUploadError() || 'Could not upload the media. Please try again.');
          return;
        }
        url = uploaded;
      }

      const patchBody = editTarget === 'avatar' ? { icon: url } : { banner: url };
      const res = await api(`/communities/${name}`, {
        method: 'PATCH',
        body: JSON.stringify(patchBody),
      });
      if (res.success) {
        // Clear any stale device-local color override for this surface.
        try { await AsyncStorage.removeItem(`communityMedia:${name}:${editTarget}`); } catch {}
        setCommunity((prev) => prev ? {
          ...prev,
          ...(editTarget === 'avatar' ? { avatar: url } : { banner: url }),
        } : prev);
        setEditOpen(false);
      } else {
        Alert.alert('Error', res.error || 'Could not save. Please try again.');
      }
    } finally {
      setSavingEdit(false);
    }
  }

  // Overflow: ONLY real, wired actions. Leave uses the existing membership
  // endpoint (DELETE /communities/:name/join) and only renders for members.
  const overflowActions: OverflowAction[] = [
    { key: 'share', label: 'Share community', icon: 'share-outline', onPress: handleShare },
    ...(isOwnerAdmin ? [{
      key: 'edit-details',
      label: 'Edit name & description',
      icon: 'create-outline' as const,
      onPress: () => {
        setDetailsName(community?.displayName || '');
        setDetailsDesc(community?.description || '');
        // rules is a Json? column — normalize either string[] or {title}[] to string[].
        setDetailsRules(Array.isArray(community?.rules)
          ? (community!.rules as any[]).map((r) => typeof r === 'string' ? r : (r?.title || r?.description || '')).filter(Boolean)
          : []);
        setDetailsOpen(true);
        setMenuOpen(false);
      },
    } satisfies OverflowAction] : []),
    ...(isOwnerAdmin ? [{
      key: 'banned',
      label: 'Banned members',
      icon: 'ban-outline' as const,
      onPress: () => { setBansOpen(true); setMenuOpen(false); fetchBans(); },
    } satisfies OverflowAction] : []),
    ...(community?.joined ? [{
      key: 'leave',
      label: 'Leave community',
      icon: 'exit-outline' as const,
      destructive: true,
      onPress: () => {
        Alert.alert('Leave Community', `Leave ${community?.displayName || community?.name}?`, [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Leave', style: 'destructive', onPress: () => { handleJoin(); } },
        ]);
      },
    } satisfies OverflowAction] : []),
  ];

  // --- Helpers / render ---
  // Compact relative timestamp (now / 12m / 5h / 3d). Returns '' for missing or
  // unparseable dates so callers can render it unconditionally.
  function formatTime(dateStr?: string | null): string {
    if (!dateStr) return '';
    const ts = new Date(dateStr).getTime();
    if (isNaN(ts)) return '';
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  }

  // Maps an announcement `type` to its Ionicon; defaults to a megaphone.
  function announcementIcon(type?: string): IoniconName {
    switch ((type || '').toLowerCase()) {
      case 'event':
        return 'calendar';
      case 'alert':
      case 'warning':
        return 'warning';
      case 'update':
        return 'sparkles';
      default:
        return 'megaphone';
    }
  }

  function renderAnnouncements() {
    const hasAny = Array.isArray(announcements) && announcements.length > 0;
    // Hide the whole section only when there's nothing AND the viewer can't post.
    if (!hasAny && !isOwnerAdmin) return null;
    return (
      <View style={styles.announceWrap}>
        <View style={styles.announceHeadingRow}>
          <Ionicons name="megaphone" size={15} color={colors.primary} />
          <Text style={styles.announceHeading}>Announcements</Text>
          {isOwnerAdmin ? (
            <TouchableOpacity
              style={styles.announceAddBtn}
              onPress={() => openAnnouncementEditor(null)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Add announcement"
            >
              <Ionicons name="add" size={16} color={colors.primary} />
              <Text style={styles.announceAddText}>Add</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {!hasAny && isOwnerAdmin ? (
          <Text style={styles.announceEmptyHint}>No announcements yet — tap Add to post the first one.</Text>
        ) : null}
        {announcements.map((a) => {
          const hasLink = !!a.link && typeof a.link === 'string';
          return (
            <GlassCard
              key={a.id}
              padding={spacing.lg}
              style={styles.announceCard}
              onPress={hasLink ? () => handleAnnouncementLink(a.link) : undefined}
              onLongPress={isOwnerAdmin ? () => announcementActions(a) : undefined}
              accessibilityRole={hasLink ? 'link' : 'text'}
              accessibilityLabel={isOwnerAdmin ? `Announcement: ${a.title || ''} — long-press to edit or delete` : `Announcement: ${a.title || ''}`}
            >
              <View style={styles.announceTopRow}>
                <View style={styles.announceChip}>
                  <Ionicons name={announcementIcon(a.type)} size={12} color={colors.primary} />
                  <Text style={styles.announceChipText}>{a.type || 'news'}</Text>
                </View>
                {a.createdAt ? (
                  <Text style={styles.announceTime}>{formatTime(a.createdAt)}</Text>
                ) : null}
              </View>
              <Text style={styles.announceTitle} numberOfLines={2}>{a.title || ''}</Text>
              {a.content ? (
                <Text style={styles.announceContent} numberOfLines={4}>{a.content}</Text>
              ) : null}
              {hasLink ? (
                <View style={styles.announceLinkRow}>
                  <Text style={styles.announceLinkText}>Learn more</Text>
                  <Ionicons name="arrow-forward" size={13} color={colors.primary} />
                </View>
              ) : null}
            </GlassCard>
          );
        })}
      </View>
    );
  }

  // Floating translucent top chrome (Apple §12): back + overflow ride over the
  // banner as real glass, respecting the safe-area top inset. Absolutely
  // positioned so the banner/content scrolls under it — never an opaque bar.
  function renderTopChrome(showOverflow: boolean) {
    return (
      <View pointerEvents="box-none" style={[styles.topChrome, { top: insets.top + spacing.xs }]}>
        <GlassSurface variant="bar" radius={radius.full} style={styles.chromeBtnWrap} contentStyle={styles.chromeBtn}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="chevron-back" size={22} color={colors.ink} />
          </TouchableOpacity>
        </GlassSurface>
        {showOverflow ? (
          <View style={styles.chromeRight}>
            {/* Owner/admin-only Edit affordance (§2 Agency): opens the banner
                editor. Hidden entirely for non-admins — the server also enforces
                the permission on PATCH, so this is a UI gate, not the only one. */}
            {isOwnerAdmin ? (
              <GlassSurface variant="bar" radius={radius.full} style={styles.chromeBtnWrap} contentStyle={styles.chromePillContent}>
                <TouchableOpacity
                  onPress={() => openEdit('banner')}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  accessibilityRole="button"
                  accessibilityLabel="Edit community banner and picture"
                  style={styles.chromePill}
                >
                  <Ionicons name="create-outline" size={17} color={colors.primary} />
                  <Text style={styles.chromePillText}>Edit</Text>
                </TouchableOpacity>
              </GlassSurface>
            ) : null}
            <GlassSurface variant="bar" radius={radius.full} style={styles.chromeBtnWrap} contentStyle={styles.chromeBtn}>
              <TouchableOpacity
                onPress={() => setMenuOpen(true)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityRole="button"
                accessibilityLabel="More options"
              >
                <Ionicons name="ellipsis-horizontal" size={20} color={colors.ink} />
              </TouchableOpacity>
            </GlassSurface>
          </View>
        ) : null}
      </View>
    );
  }

  // Renders the banner surface content honestly for its 3 kinds: solid color
  // token, video url (inline muted loop), or image url; falls back to the
  // deterministic brand gradient. Shared by the header + the edit-sheet preview.
  function renderBannerFill(value: string | null | undefined, grad: [string, string]) {
    const colorHex = colorFromToken(value);
    if (colorHex) {
      return <View style={[styles.banner, { backgroundColor: colorHex }]} />;
    }
    if (isVideoUrl(value) && RNVideo) {
      return (
        <RNVideo
          source={{ uri: value as string }}
          style={styles.banner}
          resizeMode="cover"
          repeat
          muted
          paused={false}
        />
      );
    }
    if (value && value.length > 0) {
      return <CachedImage source={{ uri: value }} style={styles.banner} resizeMode="cover" />;
    }
    return <LinearGradient colors={grad} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.banner} />;
  }

  // Avatar equivalent of renderBannerFill (circular; the letter fallback is
  // preserved for the no-media / gradient case).
  function renderAvatarFill(value: string | null | undefined, grad: [string, string], initial: string, nameLike?: string, displayName?: string) {
    const colorHex = colorFromToken(value);
    if (colorHex) {
      return (
        <View style={[styles.communityAvatar, styles.avatarFallback, { backgroundColor: colorHex }]}>
          <Text style={styles.avatarLetter}>{initial}</Text>
        </View>
      );
    }
    if (isVideoUrl(value) && RNVideo) {
      return (
        <RNVideo source={{ uri: value as string }} style={styles.communityAvatar} resizeMode="cover" repeat muted paused={false} />
      );
    }
    // Resolve a real logo: custom upload → coin logo (by name) → PNG-safe stored.
    const uri = communityIcon(nameLike, displayName, value || undefined);
    if (uri) {
      return <CachedImage source={{ uri }} style={styles.communityAvatar} />;
    }
    return (
      <LinearGradient colors={grad} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={[styles.communityAvatar, styles.avatarFallback]}>
        <Text style={styles.avatarLetter}>{initial}</Text>
      </LinearGradient>
    );
  }

  function renderHeader() {
    if (!community) return null;
    const grad = gradientFor(community.name || community.displayName || '');
    const initial = (community.displayName || community.name || '?').charAt(0).toUpperCase();
    // When no banner/avatar media is set, fall back to the device-local card
    // color chosen at creation (as a `color:` token) before the brand gradient.
    const localToken = localColor ? `${COLOR_PREFIX}${localColor}` : null;
    // Resolve images through the shared lib/icons helpers (same as discover): a
    // deliberate custom upload wins; else a real coin logo / topic photo. This is
    // what fixes the "images off" header — the raw API icon is a dicebear SVG that
    // RN's <Image> can't render. A device-local color token still overrides.
    const rawBanner = community.banner && community.banner.length > 0 ? community.banner : null;
    const bannerValue =
      rawBanner && rawBanner.startsWith(COLOR_PREFIX)
        ? rawBanner
        : (localToken && !rawBanner)
          ? localToken
          : communityCover(community.name, community.displayName, rawBanner ?? undefined, 800);
    return (
      <View>
        {/* Banner — custom image/video/solid color, or the community's
            deterministic brand gradient. A bottom scrim keeps the overlapping
            avatar + name legible. */}
        <View style={styles.bannerContainer}>
          {renderBannerFill(bannerValue, grad)}
          {/* Owner/admin quick tap-to-edit the banner (§16 mapping: the control
              sits on the thing it changes). Hidden for non-admins. */}
          {isOwnerAdmin ? (
            <TouchableOpacity
              onPress={() => openEdit('banner')}
              activeOpacity={0.85}
              style={styles.bannerEditBadge}
              accessibilityRole="button"
              accessibilityLabel="Edit banner"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="camera" size={15} color="#FFFFFF" />
            </TouchableOpacity>
          ) : null}
          {/* Top scrim — keeps the floating glass chrome legible over bright banners. */}
          <LinearGradient
            colors={['rgba(26,26,46,0.28)', 'rgba(26,26,46,0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.bannerScrimTop}
          />
          <LinearGradient
            colors={['rgba(26,26,46,0)', 'rgba(26,26,46,0.35)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.bannerScrim}
          />
        </View>

        {/* Community identity card — floats over the banner with real glass depth.
            No avatar circle here (#76): the banner already carries the artwork; a
            cropped circle over it read as broken. Clean centered identity instead. */}
        <GlassCard style={styles.identityCard} padding={spacing.lg}>
          <Text style={styles.communityName} numberOfLines={2}>{community.displayName || community.name || ''}</Text>
          <Text style={styles.communityHandle} numberOfLines={1}>c/{community.name || ''}</Text>

          {/* Member avatar-stack + real count (shared discover primitive). */}
          <View style={styles.memberStackRow}>
            <AvatarStack
              seed={community.name || community.displayName || ''}
              count={community.memberCount ?? 0}
              size={24}
              onDark={false}
            />
          </View>

          {/* Join / Joined CTA — full-width glass button, never clipped. Disabled +
              spinner while a join/leave is in flight (blocks the double-tap race). */}
          <GlassButton
            title={community.joined ? 'Joined' : 'Join Community'}
            icon={community.joined ? 'checkmark-circle-outline' : 'add-circle-outline'}
            variant={community.joined ? 'secondary' : 'primary'}
            onPress={handleJoin}
            loading={joining}
            disabled={joining}
            fullWidth
            style={styles.joinCta}
          />
        </GlassCard>

        {/* Community announcements */}
        {renderAnnouncements()}

        {/* Tabs — segmented underline row (shared community primitive), uniform
            with the ServerDetail interior. */}
        <View style={styles.tabsWrap}>
          <SegmentedTabs<TabKey>
            tabs={TABS}
            active={activeTab}
            onChange={setActiveTab}
          />
        </View>
      </View>
    );
  }

  function renderFeedItem({ item }: { item: PostItem }) {
    return (
      <GlassCard
        style={styles.postCard}
        padding={spacing.lg}
        onPress={() => navigation.navigate('Post', { id: item.id })}
        accessibilityRole="button"
        accessibilityLabel={`Post by ${item.author?.displayName || item.author?.username || 'user'}`}
      >
        <View style={styles.postAuthorRow}>
          {item.author?.avatar ? (
            <CachedImage source={{ uri: item.author.avatar }} style={styles.postAvatar} />
          ) : (
            <View style={[styles.postAvatar, { backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' }]}>
              <Text style={{ color: '#fff', fontWeight: '700' }}>
                {(item.author?.displayName || item.author?.username || '?').charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
          <View style={{ flex: 1 }}>
            <Text style={styles.postAuthorName} numberOfLines={1}>{item.author?.displayName || item.author?.username || 'Unknown'}</Text>
            <Text style={styles.postAuthorHandle} numberOfLines={1}>@{item.author?.username || 'unknown'}</Text>
          </View>
          <Text style={styles.postTime}>{formatTime(item.createdAt)}</Text>
          <TouchableOpacity
            onPress={() => setTsTarget({
              type: 'post',
              contentId: item.id,
              authorId: item.author?.id,
              authorUsername: item.author?.username,
              authorLabel: `@${item.author?.username || 'user'}`,
            })}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel="Post options — report or block"
            style={{ marginLeft: spacing.sm }}
          >
            <Ionicons name="ellipsis-horizontal" size={16} color={colors.muted} />
          </TouchableOpacity>
        </View>
        {!!item.title && (
          <Text style={styles.postTitle} numberOfLines={2}>{item.title}</Text>
        )}
        <Text style={styles.postContent} numberOfLines={4}>
          {item.content || ''}
        </Text>
        <View style={styles.postStats}>
          <View style={styles.postStatItem}>
            <Ionicons name="heart-outline" size={14} color={colors.muted} />
            <Text style={styles.postStatText}>{(item.likes ?? 0).toLocaleString()}</Text>
          </View>
          <View style={styles.postStatItem}>
            <Ionicons name="chatbubble-outline" size={14} color={colors.muted} />
            <Text style={styles.postStatText}>{(item.comments ?? 0).toLocaleString()}</Text>
          </View>
        </View>
      </GlassCard>
    );
  }

  // Owner/admin: long-press a member → Remove (kick) or Ban.
  //  · Remove → DELETE /communities/:name/members/:username (can rejoin)
  //  · Ban    → POST   /communities/:name/members/:username/ban (persistent; 403 on rejoin)
  function confirmKickMember(m: { username: string; displayName: string }) {
    if (!m.username) return;
    const label = m.displayName || m.username;
    Alert.alert(
      label,
      `Manage this member of ${community?.displayName || community?.name}.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            const res = await api(`/communities/${name}/members/${encodeURIComponent(m.username)}`, { method: 'DELETE' });
            if (res.success) fetchMembers();
            else Alert.alert('Error', (res as any).error || 'Could not remove this member.');
          },
        },
        {
          text: 'Ban',
          style: 'destructive',
          onPress: () => Alert.alert(
            'Ban member',
            `Ban ${label}? They'll be removed and can't rejoin until you lift the ban.`,
            [
              { text: 'Cancel', style: 'cancel' },
              {
                text: 'Ban',
                style: 'destructive',
                onPress: async () => {
                  const res = await api(`/communities/${name}/members/${encodeURIComponent(m.username)}/ban`, { method: 'POST', body: JSON.stringify({}) });
                  if (res.success) fetchMembers();
                  else Alert.alert('Error', (res as any).error || 'Could not ban this member.');
                },
              },
            ],
          ),
        },
      ],
    );
  }

  function renderMemberItem({ item }: { item: Member }) {
    const m = memberIdentity(item);
    // Owner/admin can kick (server also blocks self-kick with a clear message).
    const canKick = isOwnerAdmin && !!m.username;
    return (
      <TouchableOpacity
        style={[styles.memberCard, { width: `${100 / memberColumns}%` }]}
        onPress={() => { if (m.username) navigation.navigate('UserProfile', { username: m.username }); }}
        onLongPress={canKick ? () => confirmKickMember(m) : undefined}
        delayLongPress={350}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={canKick
          ? `${m.displayName || m.username || 'member'} — tap to view, long-press to remove`
          : `View profile of ${m.displayName || m.username || 'member'}`}
      >
        <GlassCard style={styles.memberCardInner} padding={spacing.md}>
          {m.avatar && m.avatar.length > 0 ? (
            <CachedImage source={{ uri: m.avatar }} style={styles.memberAvatar} />
          ) : (
            <View style={[styles.memberAvatar, styles.memberAvatarFallback]}>
              <Text style={styles.memberAvatarLetter}>{(m.displayName || m.username || '?').charAt(0).toUpperCase()}</Text>
            </View>
          )}
          <Text style={styles.memberName} numberOfLines={1}>
            {m.displayName || m.username || ''}
          </Text>
          {m.role ? <Text style={styles.memberRole} numberOfLines={1}>{m.role}</Text> : null}
        </GlassCard>
      </TouchableOpacity>
    );
  }

  function renderLeaderboardItem({ item }: { item: LeaderboardEntry }) {
    const medalColors: Record<number, string> = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
    // Proportion each member's points against the leader's (top of the sorted
    // board), so the track reads as "share of the #1 score" — the macro-tracker
    // labeled-row pattern applied to a real, live surface. Guard the divide so
    // an all-zero (or empty) board never yields NaN.
    const leaderPoints = Array.isArray(leaderboard)
      ? leaderboard.reduce((m, e) => Math.max(m, e?.points ?? 0), 0)
      : 0;
    const share = leaderPoints > 0 ? (item.points ?? 0) / leaderPoints : 0;
    const medalTint = item.rank <= 3 ? medalColors[item.rank] : undefined;
    return (
      <GlassCard style={styles.leaderRow} padding={spacing.md} contentStyle={styles.leaderRowStack}>
        <View style={styles.leaderRowInner}>
          <View style={styles.rankBadge}>
            <Text
              style={[
                styles.rankText,
                item.rank <= 3 && { color: medalColors[item.rank] || colors.ink },
              ]}
            >
              #{item.rank}
            </Text>
          </View>
          {item.avatar && item.avatar.length > 0 ? (
            <CachedImage source={{ uri: item.avatar }} style={styles.leaderAvatar} />
          ) : (
            <View style={[styles.leaderAvatar, styles.memberAvatarFallback]}>
              <Text style={styles.memberAvatarLetter}>{(item.displayName || item.username || '?').charAt(0).toUpperCase()}</Text>
            </View>
          )}
          <View style={{ flex: 1 }}>
            <Text style={styles.leaderName} numberOfLines={1}>{item.displayName || item.username || ''}</Text>
            <Text style={styles.leaderHandle} numberOfLines={1}>@{item.username || 'unknown'}</Text>
          </View>
          <View style={styles.pointsBadge}>
            <Text style={styles.pointsText}>{(item.points ?? 0).toLocaleString()} pts</Text>
          </View>
        </View>
        {/* Share-of-leader track — labeled row with a status-tinted fill + % pill. */}
        <ProgressRow
          label="Share of #1"
          progress={share}
          color={medalTint ?? colors.primary}
          style={styles.leaderProgress}
        />
      </GlassCard>
    );
  }

  function renderAbout() {
    if (!community) return null;
    return (
      <View style={styles.aboutContainer}>
        <GlassCard style={styles.aboutSection} padding={spacing.lg}>
          <Text style={styles.aboutTitle}>Description</Text>
          <Text style={styles.aboutText}>{community.description || 'No description yet.'}</Text>
        </GlassCard>

        {Array.isArray(community.rules) && community.rules.length > 0 && (
          <GlassCard style={styles.aboutSection} padding={spacing.lg}>
            <Text style={styles.aboutTitle}>Rules</Text>
            {community.rules.map((rule, i) => (
              <View key={i} style={styles.ruleRow}>
                <Text style={styles.ruleNumber}>{i + 1}.</Text>
                <Text style={styles.ruleText}>{rule}</Text>
              </View>
            ))}
          </GlassCard>
        )}

        <GlassCard style={styles.aboutSection} padding={spacing.lg}>
          <Text style={styles.aboutTitle}>Created</Text>
          <Text style={styles.aboutText}>
            {community.createdAt
              ? (() => {
                  const d = new Date(community.createdAt);
                  return isNaN(d.getTime())
                    ? ''
                    : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                })()
              : ''}
          </Text>
        </GlassCard>
      </View>
    );
  }

  // The three list-backed tabs (feed/members/leaderboard) share one FlatList.
  // These memoized selectors return the right data array, renderItem and
  // keyExtractor for the active tab; "about" returns empty and is rendered
  // separately via its own ScrollView branch below.
  const getTabData = useCallback(() => {
    switch (activeTab) {
      case 'feed':
        return (Array.isArray(posts) ? posts : []).filter((p) => !(isBlocked(p?.author?.id, p?.author?.username) || isMuted(p?.author?.id, p?.author?.username)));
      case 'members':
        return Array.isArray(members) ? members : [];
      case 'leaderboard':
        return Array.isArray(leaderboard) ? leaderboard : [];
      default:
        return [];
    }
  }, [activeTab, posts, members, leaderboard]);

  const getTabRenderItem = useCallback(() => {
    switch (activeTab) {
      case 'feed':
        return renderFeedItem;
      case 'members':
        return renderMemberItem as any;
      case 'leaderboard':
        return renderLeaderboardItem as any;
      default:
        return () => null;
    }
  }, [activeTab]);

  const getTabKeyExtractor = useCallback(() => {
    switch (activeTab) {
      case 'feed':
        return (item: any, i: number) => item?.id ?? `post-${i}`;
      case 'members':
        // Stable index-based fallback (never Math.random(), which remounts rows
        // every render and breaks scroll/selection when ids are all absent).
        return (item: any, i: number) => item?.id ?? item?.userId ?? item?.User?.username ?? `member-${i}`;
      case 'leaderboard':
        return (item: any) => `${item.rank}-${item.username}`;
      default:
        return (item: any, index: number) => `${index}`;
    }
  }, [activeTab]);

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        {renderTopChrome(false)}
        <LoadingState message="Loading…" onRetry={fetchCommunity} />
      </View>
    );
  }

  if (!community) {
    // Offline / transient failure (network, 5xx, silent auth) → Retry. Only a
    // genuine 404 shows the terminal "not found" with Go back (mirrors
    // ServerDetail's offline vs notfound branching) — a transient blip must never
    // render a permanent "Community not found".
    const isOffline = fetchError !== 'notfound';
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        {renderTopChrome(false)}
        <View style={styles.loader}>
          <View style={styles.emptyIconCircle}>
            <Ionicons name={isOffline ? 'cloud-offline-outline' : 'alert-circle-outline'} size={28} color={colors.primary} />
          </View>
          <Text style={styles.errorText}>
            {isOffline ? "Couldn't load this community" : 'Community not found'}
          </Text>
          {isOffline ? (
            <Text style={styles.emptySub}>Check your connection and try again.</Text>
          ) : null}
          {isOffline ? (
            <TouchableOpacity
              onPress={fetchCommunity}
              accessibilityRole="button"
              accessibilityLabel="Retry loading community"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.backLink}>Retry</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              accessibilityRole="button"
              accessibilityLabel="Go back"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.backLink}>Go back</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  const overflowMenu = (
    <OverflowMenu
      visible={menuOpen}
      onClose={() => setMenuOpen(false)}
      title={community.displayName || community.name}
      actions={overflowActions}
    />
  );

  // Owner/admin edit sheet: image / video / solid-color for the picture OR
  // banner. Bottom-anchored translucent glass (§12), light glass everywhere.
  const editGrad = gradientFor(community.name || community.displayName || '');
  const editInitial = (community.displayName || community.name || '?').charAt(0).toUpperCase();

  // Save name & description (owner/admin) via PATCH /communities/:name.
  async function saveDetails() {
    const displayName = detailsName.trim();
    if (!displayName) { Alert.alert('Name required', 'Give the community a display name.'); return; }
    const rules = detailsRules.map((r) => r.trim()).filter(Boolean);
    setSavingDetails(true);
    try {
      const res = await api(`/communities/${name}`, {
        method: 'PATCH',
        body: JSON.stringify({ displayName, description: detailsDesc.trim(), rules }),
      });
      if (res.success) {
        setCommunity((prev) => prev ? { ...prev, displayName, description: detailsDesc.trim(), rules } : prev);
        setDetailsOpen(false);
      } else {
        Alert.alert('Error', (res as any).error || 'Could not save changes.');
      }
    } finally {
      setSavingDetails(false);
    }
  }

  const detailsSheet = (
    <Modal visible={detailsOpen} transparent animationType="slide" onRequestClose={() => setDetailsOpen(false)}>
      <Pressable style={styles.editBackdrop} onPress={() => setDetailsOpen(false)} />
      <View style={styles.editSheetWrap} pointerEvents="box-none">
        <GlassSurface
          variant="sheet"
          radius={radius.xl}
          style={styles.editSheet}
          contentStyle={[styles.editSheetContent, { paddingBottom: insets.bottom + spacing.lg }]}
        >
          <View style={styles.editHandle} />
          <Text style={styles.editTitle}>Edit community</Text>
          <Text style={styles.editSub}>Update the display name and description.</Text>

          <Text style={styles.detailsLabel}>Name</Text>
          <TextInput
            style={styles.detailsInput}
            value={detailsName}
            onChangeText={setDetailsName}
            placeholder="Community name"
            placeholderTextColor={colors.muted}
            maxLength={100}
            returnKeyType="next"
          />

          <Text style={styles.detailsLabel}>Description</Text>
          <TextInput
            style={[styles.detailsInput, styles.detailsTextarea]}
            value={detailsDesc}
            onChangeText={setDetailsDesc}
            placeholder="What is this community about?"
            placeholderTextColor={colors.muted}
            maxLength={1000}
            multiline
          />

          <Text style={styles.detailsLabel}>Rules</Text>
          {detailsRules.map((rule, i) => (
            <View key={i} style={styles.ruleEditRow}>
              <Text style={styles.ruleEditNum}>{i + 1}.</Text>
              <TextInput
                style={[styles.detailsInput, styles.ruleEditInput]}
                value={rule}
                onChangeText={(t) => setDetailsRules((prev) => prev.map((r, j) => j === i ? t : r))}
                placeholder={`Rule ${i + 1}`}
                placeholderTextColor={colors.muted}
                maxLength={500}
              />
              <TouchableOpacity
                onPress={() => setDetailsRules((prev) => prev.filter((_, j) => j !== i))}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel={`Remove rule ${i + 1}`}
              >
                <Ionicons name="close-circle" size={22} color={colors.muted} />
              </TouchableOpacity>
            </View>
          ))}
          {detailsRules.length < 15 ? (
            <TouchableOpacity
              style={styles.ruleAddRow}
              onPress={() => setDetailsRules((prev) => [...prev, ''])}
              accessibilityRole="button"
              accessibilityLabel="Add a rule"
            >
              <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
              <Text style={styles.ruleAddText}>Add rule</Text>
            </TouchableOpacity>
          ) : null}

          <GlassButton
            title={savingDetails ? 'Saving…' : 'Save changes'}
            onPress={saveDetails}
            disabled={savingDetails}
            fullWidth
            style={styles.editPickBtn}
          />
        </GlassSurface>
      </View>
    </Modal>
  );

  // Announcements: open editor (create when a=null, else prefill for edit).
  function openAnnouncementEditor(a: CommunityAnnouncement | null) {
    setAnnEditId(a?.id ?? null);
    setAnnTitle(a?.title ?? '');
    setAnnContent(a?.content ?? '');
    setAnnOpen(true);
    setMenuOpen(false);
  }
  // Long-press an announcement (owner) → edit or delete.
  function announcementActions(a: CommunityAnnouncement) {
    Alert.alert(a.title || 'Announcement', undefined, [
      { text: 'Edit', onPress: () => openAnnouncementEditor(a) },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          const res = await api(`/communities/${name}/announcements/${a.id}`, { method: 'DELETE' });
          if (res.success) fetchAnnouncements();
          else Alert.alert('Error', (res as any).error || 'Could not delete.');
        },
      },
      { text: 'Cancel', style: 'cancel' },
    ]);
  }
  async function saveAnnouncement() {
    const title = annTitle.trim();
    const content = annContent.trim();
    if (!title || !content) { Alert.alert('Required', 'Title and content are both required.'); return; }
    setSavingAnn(true);
    try {
      const res = annEditId
        ? await api(`/communities/${name}/announcements/${annEditId}`, { method: 'PATCH', body: JSON.stringify({ title, content }) })
        : await api(`/communities/${name}/announcements`, { method: 'POST', body: JSON.stringify({ title, content }) });
      if (res.success) { setAnnOpen(false); fetchAnnouncements(); }
      else Alert.alert('Error', (res as any).error || 'Could not save the announcement.');
    } finally {
      setSavingAnn(false);
    }
  }

  const announcementSheet = (
    <Modal visible={annOpen} transparent animationType="slide" onRequestClose={() => setAnnOpen(false)}>
      <Pressable style={styles.editBackdrop} onPress={() => setAnnOpen(false)} />
      <View style={styles.editSheetWrap} pointerEvents="box-none">
        <GlassSurface
          variant="sheet"
          radius={radius.xl}
          style={styles.editSheet}
          contentStyle={[styles.editSheetContent, { paddingBottom: insets.bottom + spacing.lg }]}
        >
          <View style={styles.editHandle} />
          <Text style={styles.editTitle}>{annEditId ? 'Edit announcement' : 'New announcement'}</Text>
          <Text style={styles.editSub}>Posted to everyone in this community.</Text>

          <Text style={styles.detailsLabel}>Title</Text>
          <TextInput
            style={styles.detailsInput}
            value={annTitle}
            onChangeText={setAnnTitle}
            placeholder="Announcement title"
            placeholderTextColor={colors.muted}
            maxLength={200}
          />
          <Text style={styles.detailsLabel}>Message</Text>
          <TextInput
            style={[styles.detailsInput, styles.detailsTextarea]}
            value={annContent}
            onChangeText={setAnnContent}
            placeholder="What do you want to tell the community?"
            placeholderTextColor={colors.muted}
            maxLength={4000}
            multiline
          />
          <GlassButton
            title={savingAnn ? 'Saving…' : annEditId ? 'Save changes' : 'Post announcement'}
            onPress={saveAnnouncement}
            disabled={savingAnn}
            fullWidth
            style={styles.editPickBtn}
          />
        </GlassSurface>
      </View>
    </Modal>
  );

  async function fetchBans() {
    setLoadingBans(true);
    try {
      const res = await api<{ bans?: any[] }>(`/communities/${name}/bans`);
      setBans(res.success && Array.isArray(res.data?.bans) ? res.data!.bans : []);
    } catch {
      setBans([]);
    } finally {
      setLoadingBans(false);
    }
  }
  async function unban(username?: string) {
    if (!username) return;
    const res = await api(`/communities/${name}/bans/${encodeURIComponent(username)}`, { method: 'DELETE' });
    if (res.success) fetchBans();
    else Alert.alert('Error', (res as any).error || 'Could not lift the ban.');
  }

  const bansSheet = (
    <Modal visible={bansOpen} transparent animationType="slide" onRequestClose={() => setBansOpen(false)}>
      <Pressable style={styles.editBackdrop} onPress={() => setBansOpen(false)} />
      <View style={styles.editSheetWrap} pointerEvents="box-none">
        <GlassSurface
          variant="sheet"
          radius={radius.xl}
          style={styles.editSheet}
          contentStyle={[styles.editSheetContent, { paddingBottom: insets.bottom + spacing.lg }]}
        >
          <View style={styles.editHandle} />
          <Text style={styles.editTitle}>Banned members</Text>
          <Text style={styles.editSub}>Banned users can't rejoin until you lift the ban.</Text>
          {loadingBans ? (
            <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.lg }} />
          ) : bans.length === 0 ? (
            <Text style={styles.announceEmptyHint}>No banned members.</Text>
          ) : (
            <ScrollView style={{ maxHeight: 320 }} showsVerticalScrollIndicator={false}>
              {bans.map((b) => (
                <View key={b.id} style={styles.banRow}>
                  {b.user?.avatar ? (
                    <CachedImage source={{ uri: b.user.avatar }} style={styles.banAvatar} />
                  ) : (
                    <View style={[styles.banAvatar, styles.memberAvatarFallback]}>
                      <Text style={styles.memberAvatarLetter}>{(b.user?.displayName || b.user?.username || '?').charAt(0).toUpperCase()}</Text>
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={styles.banName} numberOfLines={1}>{b.user?.displayName || b.user?.username || 'Unknown'}</Text>
                    {b.reason ? <Text style={styles.banReason} numberOfLines={1}>{b.reason}</Text> : null}
                  </View>
                  <TouchableOpacity
                    style={styles.unbanBtn}
                    onPress={() => unban(b.user?.username)}
                    accessibilityRole="button"
                    accessibilityLabel={`Unban ${b.user?.username || 'user'}`}
                  >
                    <Text style={styles.unbanBtnText}>Unban</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          )}
        </GlassSurface>
      </View>
    </Modal>
  );

  const editSheet = (
    <Modal visible={editOpen} transparent animationType="slide" onRequestClose={() => setEditOpen(false)}>
      <Pressable style={styles.editBackdrop} onPress={() => setEditOpen(false)} />
      <View style={styles.editSheetWrap} pointerEvents="box-none">
        <GlassSurface
          variant="sheet"
          radius={radius.xl}
          style={styles.editSheet}
          contentStyle={[styles.editSheetContent, { paddingBottom: insets.bottom + spacing.lg }]}
        >
          <View style={styles.editHandle} />
          <Text style={styles.editTitle}>
            {editTarget === 'avatar' ? 'Community picture' : 'Community banner'}
          </Text>
          <Text style={styles.editSub}>Set an image, a video, or a solid color.</Text>

          {/* Live preview of the pending value */}
          <View style={styles.editPreviewWrap}>
            {editTarget === 'banner'
              ? renderBannerFill(pendingValue, editGrad)
              : (
                <View style={styles.editAvatarPreview}>
                  {renderAvatarFill(pendingValue, editGrad, editInitial)}
                </View>
              )}
          </View>

          {/* Image / video picker (mediaType 'mixed') */}
          <GlassButton
            title="Choose photo or video"
            icon="image-outline"
            variant="secondary"
            onPress={pickEditMedia}
            fullWidth
            style={styles.editPickBtn}
          />

          {/* Solid color palette — reuses the CoinCommunityCard SOLIDS. Selecting
              a swatch sets a `color:` token; saved device-local (honest note). */}
          <Text style={styles.editColorLabel}>Or pick a solid color</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.editSwatchStrip}>
            {CARD_SOLIDS.map((c) => {
              const token = `${COLOR_PREFIX}${c}`;
              const selected = pendingValue === token;
              return (
                <Pressable
                  key={c}
                  onPress={() => setPendingValue(token)}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel="Solid color"
                  style={({ pressed }) => [
                    styles.editSwatch,
                    { backgroundColor: c },
                    selected && styles.editSwatchSelected,
                    pressed && { transform: [{ scale: 0.92 }] },
                  ]}
                >
                  {selected ? <Ionicons name="checkmark" size={16} color="#FFFFFF" /> : null}
                </Pressable>
              );
            })}
          </ScrollView>
          <Text style={styles.editColorNote}>
            Solid colors are saved on this device — image & video sync to everyone.
          </Text>

          <View style={styles.editActions}>
            <GlassButton
              title="Cancel"
              variant="ghost"
              onPress={() => setEditOpen(false)}
              style={styles.editActionBtn}
            />
            <GlassButton
              title="Save"
              icon="checkmark-circle-outline"
              onPress={saveEdit}
              loading={savingEdit}
              disabled={savingEdit || pendingValue == null}
              style={styles.editActionBtn}
            />
          </View>
        </GlassSurface>
      </View>
    </Modal>
  );

  // Channels-model interior (uniform with ServerDetail, reference IMG_5877):
  // pinned/featured tiles + grouped 2-column channel grids.
  if (activeTab === 'channels') {
    const pinned = pickPinned(sections);
    const pinnedIds = new Set(pinned.map((p) => p.id));
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={[styles.listContent, containerStyle]}>
          {renderHeader()}
          <View style={styles.channelsBody}>
            {isOwnerAdmin ? (
              <TouchableOpacity
                style={styles.channelAddRow}
                onPress={addChannel}
                accessibilityRole="button"
                accessibilityLabel="Add a channel"
              >
                <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
                <Text style={styles.channelAddText}>Add channel</Text>
              </TouchableOpacity>
            ) : null}
            {pinned.length > 0 && (
              <View style={styles.pinnedRow}>
                {pinned.map((c) => (
                  <PinnedChannelTile key={c.id} channel={c} onPress={() => openChannel(c)} onLongPress={isOwnerAdmin ? () => confirmDeleteChannel(c) : undefined} />
                ))}
              </View>
            )}

            {sections.map((section) => {
              const gridChannels = section.channels.filter((c) => !pinnedIds.has(c.id));
              if (gridChannels.length === 0) return null;
              return (
                <View key={section.name} style={styles.section}>
                  <Text style={styles.sectionTitle}>{section.name}</Text>
                  <View style={styles.grid}>
                    {gridChannels.map((c) => (
                      <ChannelGridTile key={c.id} channel={c} onPress={() => openChannel(c)} onLongPress={isOwnerAdmin ? () => confirmDeleteChannel(c) : undefined} />
                    ))}
                  </View>
                </View>
              );
            })}

            {sections.length === 0 && (
              <View style={styles.emptyWrap}>
                <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                  <View style={styles.emptyIconCircle}>
                    <Ionicons name="chatbubbles-outline" size={28} color={colors.primary} />
                  </View>
                  <Text style={styles.emptyText}>No channels yet</Text>
                  <Text style={styles.emptySub}>Channels will appear here once they're created.</Text>
                </GlassCard>
              </View>
            )}
          </View>
        </ScrollView>
        {renderTopChrome(true)}
        {overflowMenu}
        {editSheet}
        {detailsSheet}
        {announcementSheet}
        {bansSheet}
      </View>
    );
  }

  if (activeTab === 'about') {
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={[styles.listContent, containerStyle]}>
          {renderHeader()}
          {renderAbout()}
        </ScrollView>
        {renderTopChrome(true)}
        {overflowMenu}
        {editSheet}
        {detailsSheet}
        {announcementSheet}
        {bansSheet}
      </View>
    );
  }

  if (activeTab === 'feed' && gated) {
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={[styles.listContent, containerStyle]}>
          {renderHeader()}
          <View style={styles.emptyWrap}>
            <GlassCard style={styles.emptyCard} padding={spacing.xl}>
              <View style={styles.emptyIconCircle}>
                <Ionicons name="lock-closed" size={28} color={colors.primary} />
              </View>
              <Text style={styles.emptyText}>Token-gated community</Text>
              <Text style={styles.emptySub}>{gated.reason}</Text>
              <TouchableOpacity
                onPress={() => fetchPosts()}
                style={{
                  marginTop: spacing.lg,
                  paddingVertical: spacing.sm,
                  paddingHorizontal: spacing.xl,
                  borderRadius: 999,
                  backgroundColor: colors.primary,
                }}
              >
                <Text style={{ color: '#fff', fontWeight: '700' }}>Re-check access</Text>
              </TouchableOpacity>
            </GlassCard>
          </View>
        </ScrollView>
        {renderTopChrome(true)}
        {overflowMenu}
        {editSheet}
        {detailsSheet}
        {announcementSheet}
        {bansSheet}
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground />
      <FlatList
        data={getTabData()}
        keyExtractor={getTabKeyExtractor()}
        renderItem={getTabRenderItem()}
        ListHeaderComponent={renderHeader}
        // Members render as a responsive grid (3 phone / 4 tablet / 5 desktop);
        // all other tabs as a single column. Changing `key` forces FlatList to
        // remount when numColumns changes (RN throws otherwise) so the switch is
        // clean — the key encodes the column count so a width change also remounts.
        numColumns={activeTab === 'members' ? memberColumns : 1}
        key={activeTab === 'members' ? `grid-${memberColumns}` : 'list'}
        columnWrapperStyle={activeTab === 'members' ? styles.memberColumnWrap : undefined}
        contentContainerStyle={[styles.listContent, containerStyle]}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          // Three distinct states so the empty message can't flash before the
          // fetch resolves and a silent failure can't stick as a false "nothing
          // yet": spinner while loading, error+retry on failure, empty only after
          // a genuinely-empty successful load.
          tabLoading ? (
            <View style={styles.emptyWrap}>
              <ActivityIndicator size="large" color={colors.primary} />
            </View>
          ) : tabError ? (
            <View style={styles.emptyWrap}>
              <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                <View style={styles.emptyIconCircle}>
                  <Ionicons name="cloud-offline-outline" size={28} color={colors.primary} />
                </View>
                <Text style={styles.emptyText}>
                  {activeTab === 'members' ? "Couldn't load members"
                    : activeTab === 'leaderboard' ? "Couldn't load the leaderboard"
                    : "Couldn't load posts"}
                </Text>
                <Text style={styles.emptySub}>{tabError}</Text>
                <TouchableOpacity
                  onPress={() => {
                    if (activeTab === 'feed') fetchPosts();
                    else if (activeTab === 'members') fetchMembers();
                    else if (activeTab === 'leaderboard') fetchLeaderboard();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Retry"
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text style={styles.backLink}>Retry</Text>
                </TouchableOpacity>
              </GlassCard>
            </View>
          ) : (
            <View style={styles.emptyWrap}>
              <GlassCard style={styles.emptyCard} padding={spacing.xl}>
                <View style={styles.emptyIconCircle}>
                  <Ionicons
                    name={activeTab === 'members' ? 'people-outline' : activeTab === 'leaderboard' ? 'trophy-outline' : 'newspaper-outline'}
                    size={28}
                    color={colors.primary}
                  />
                </View>
                <Text style={styles.emptyText}>
                  {activeTab === 'members' ? 'No members yet'
                    : activeTab === 'leaderboard' ? 'No ranking yet'
                    : 'No posts yet'}
                </Text>
                <Text style={styles.emptySub}>
                  {activeTab === 'feed' ? 'Be the first to post in this community.' : 'Check back soon.'}
                </Text>
              </GlassCard>
            </View>
          )
        }
      />
      <ReportBlockSheet
        visible={!!tsTarget}
        target={tsTarget}
        onClose={() => setTsTarget(null)}
        onBlocked={() => setTsTarget(null)}
      />
      {renderTopChrome(true)}
      {overflowMenu}
      {editSheet}
      {detailsSheet}
      {announcementSheet}
      {bansSheet}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    // Transparent so the full-bleed aurora ParticleBackground shows through
    // (design-system standard) instead of a flat colors.bg.
    backgroundColor: 'transparent',
  },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: spacing.sm,
    padding: spacing.xl,
  },
  // --- Floating translucent top chrome (over the banner / aurora) ---
  topChrome: {
    position: 'absolute',
    left: layoutTokens.gutter,
    right: layoutTokens.gutter,
    flexDirection: 'row',
    justifyContent: 'space-between',
    zIndex: 20,
  },
  chromeBtnWrap: {
    ...shadowTokens.card,
  },
  chromeBtn: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chromeRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  chromePillContent: {
    height: 40,
    justifyContent: 'center',
  },
  chromePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: spacing.md,
    height: 40,
  },
  chromePillText: {
    ...typographyScale.caption,
    ...fonts.semibold,
    color: colors.primary,
  },
  errorText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  backLink: {
    ...typographyScale.body,
    color: colors.primary,
    marginTop: spacing.md,
    ...fonts.semibold,
  },
  listContent: {
    // Clear the floating glass nav so the last row is visible.
    paddingBottom: layoutTokens.bottomNavInset,
  },
  // --- Channels-model interior (uniform with ServerDetail) ---
  channelsBody: { paddingHorizontal: layoutTokens.gutter, paddingTop: spacing.md },
  channelAddRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingVertical: spacing.sm,
    marginBottom: spacing.sm,
  },
  channelAddText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  banRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
  },
  banAvatar: { width: 36, height: 36, borderRadius: 18 },
  banName: { ...typographyScale.body, color: colors.ink, ...fonts.semibold },
  banReason: { ...typographyScale.caption, color: colors.muted },
  unbanBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  unbanBtnText: { ...typographyScale.caption, color: colors.primary, ...fonts.semibold },
  pinnedRow: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.xl },
  section: { marginBottom: spacing.xl },
  sectionTitle: { ...typographyScale.h3, color: colors.ink, marginBottom: spacing.md, marginLeft: 2 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', rowGap: spacing.md, columnGap: spacing.md, justifyContent: 'space-between' },
  // --- Banner + identity header ---
  bannerContainer: {
    // Taller now that the banner bleeds full-bleed under the status bar (the
    // native header no longer reserves a strip above it).
    height: 184,
    backgroundColor: colors.faint,
  },
  banner: {
    width: '100%',
    height: '100%',
  },
  // Owner/admin quick-edit badge over the banner (bottom-right, above scrims).
  bannerEditBadge: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    width: 34,
    height: 34,
    borderRadius: radius.full,
    backgroundColor: 'rgba(26,26,46,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
    zIndex: 5,
  },
  // Camera seat on the avatar ring (bottom-right).
  avatarEditBadge: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 26,
    height: 26,
    borderRadius: radius.full,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.white,
  },
  bannerScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  bannerScrimTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 96,
  },
  identityCard: {
    marginHorizontal: layoutTokens.gutter,
    // Overlap the banner so the card lifts the identity off the artwork (no
    // avatar circle now, so a lighter overlap reads cleaner).
    marginTop: -spacing.xl,
    alignItems: 'center',
  },
  avatarWrap: {
    // Ring around the avatar reads as a lens seat on the glass card.
    marginTop: -spacing.xxxl - spacing.sm,
    borderRadius: radius.full,
    borderWidth: 4,
    borderColor: colors.white,
    ...shadowTokens.card,
  },
  communityAvatar: {
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: colors.faint,
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    color: colors.white,
    ...typographyScale.h2,
  },
  communityName: {
    ...typographyScale.h2,
    color: colors.ink,
    marginTop: spacing.md,
    textAlign: 'center',
    // §15: tighten tracking on large display type so letters don't read apart.
    letterSpacing: -0.4,
  },
  communityHandle: {
    ...typographyScale.body,
    color: colors.muted,
    marginTop: 2,
  },
  memberStackRow: {
    marginTop: spacing.md,
  },
  joinCta: {
    marginTop: spacing.lg,
  },
  tabsWrap: {
    marginTop: spacing.lg,
  },
  // --- Feed ---
  postCard: {
    marginHorizontal: layoutTokens.gutter,
    marginTop: spacing.md,
  },
  postAuthorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  postAvatar: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: colors.faint,
    marginRight: spacing.md,
  },
  postAuthorName: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.semibold,
  },
  postAuthorHandle: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.regular,
  },
  postTime: {
    ...typographyScale.caption,
    color: colors.faint,
    ...fonts.regular,
  },
  postTitle: {
    ...typographyScale.body,
    ...fonts.bold,
    color: colors.ink,
    marginBottom: spacing.xs,
  },
  postContent: {
    ...typographyScale.body,
    color: colors.ink,
  },
  postStats: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginTop: spacing.md,
  },
  postStatItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  postStatText: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.regular,
  },
  // --- Members grid ---
  memberColumnWrap: {
    paddingHorizontal: layoutTokens.gutter - spacing.xs,
  },
  memberCard: {
    padding: spacing.xs,
  },
  memberCardInner: {
    alignItems: 'center',
  },
  memberAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.faint,
    marginBottom: spacing.sm,
  },
  memberAvatarFallback: {
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  memberAvatarLetter: {
    color: colors.white,
    ...fonts.bold,
    fontSize: 15,
  },
  memberName: {
    ...typographyScale.caption,
    ...fonts.semibold,
    color: colors.ink,
    textAlign: 'center',
  },
  memberRole: {
    ...typographyScale.caption,
    color: colors.primary,
    marginTop: 2,
  },
  // --- About ---
  aboutContainer: {
    paddingHorizontal: layoutTokens.gutter,
    paddingTop: spacing.lg,
  },
  aboutSection: {
    marginBottom: spacing.md,
  },
  aboutTitle: {
    ...typographyScale.h3,
    color: colors.ink,
    marginBottom: spacing.sm,
  },
  aboutText: {
    ...typographyScale.body,
    color: colors.ink2,
  },
  ruleRow: {
    flexDirection: 'row',
    marginBottom: spacing.sm,
  },
  ruleNumber: {
    ...typographyScale.body,
    ...fonts.bold,
    color: colors.primary,
    width: 24,
  },
  ruleText: {
    flex: 1,
    ...typographyScale.body,
    color: colors.ink2,
  },
  // --- Leaderboard ---
  leaderRow: {
    marginHorizontal: layoutTokens.gutter,
    marginTop: spacing.sm,
  },
  leaderRowStack: {
    flexDirection: 'column',
  },
  leaderRowInner: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  leaderProgress: {
    marginTop: spacing.sm,
  },
  rankBadge: {
    width: 36,
    alignItems: 'center',
  },
  rankText: {
    ...typographyScale.h3,
    color: colors.ink,
  },
  leaderAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.faint,
    marginRight: spacing.md,
  },
  leaderName: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
  },
  leaderHandle: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.regular,
  },
  pointsBadge: {
    backgroundColor: colors.primary + '14',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radius.full,
  },
  pointsText: {
    ...typographyScale.caption,
    ...fonts.bold,
    color: colors.primary,
  },
  // --- Announcements ---
  announceWrap: {
    paddingHorizontal: layoutTokens.gutter,
    paddingTop: spacing.lg,
  },
  announceHeadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  announceHeading: {
    ...typographyScale.caption,
    ...fonts.bold,
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  announceCard: {
    marginBottom: spacing.sm,
  },
  announceTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  announceChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.primary + '14',
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radius.full,
  },
  announceChipText: {
    ...typographyScale.caption,
    ...fonts.semibold,
    color: colors.primary,
    textTransform: 'capitalize',
  },
  announceTime: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.regular,
  },
  announceTitle: {
    ...typographyScale.body,
    ...fonts.bold,
    color: colors.ink,
  },
  announceContent: {
    ...typographyScale.caption,
    color: colors.ink2,
    marginTop: 2,
    lineHeight: 19,
  },
  announceLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: spacing.sm,
  },
  announceLinkText: {
    ...typographyScale.caption,
    ...fonts.semibold,
    color: colors.primary,
  },
  // --- Empty ---
  emptyWrap: {
    alignItems: 'center',
    paddingTop: spacing.xxxl,
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 60,
    height: 60,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySub: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  // --- Owner/admin edit sheet (image / video / solid color) ---
  editBackdrop: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(26,26,46,0.35)',
  },
  editSheetWrap: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  editSheet: {
    marginHorizontal: spacing.sm,
    marginBottom: spacing.sm,
    ...shadowTokens.card,
  },
  editSheetContent: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
  },
  editHandle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: radius.full,
    backgroundColor: colors.faint,
    marginBottom: spacing.md,
  },
  editTitle: {
    ...typographyScale.h3,
    color: colors.ink,
    letterSpacing: -0.2,
  },
  editSub: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    marginBottom: spacing.md,
  },
  detailsLabel: {
    ...typographyScale.caption,
    color: colors.ink2,
    ...fonts.semibold,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  detailsInput: {
    backgroundColor: colors.white,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    fontSize: 15,
    color: colors.ink,
    ...fonts.regular,
  },
  detailsTextarea: {
    minHeight: 90,
    textAlignVertical: 'top',
  },
  announceAddBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginLeft: 'auto',
    paddingVertical: 2,
    paddingHorizontal: spacing.xs,
  },
  announceAddText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  announceEmptyHint: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.sm,
  },
  ruleEditRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  ruleEditNum: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.semibold,
    width: 18,
  },
  ruleEditInput: {
    flex: 1,
    paddingVertical: spacing.sm,
  },
  ruleAddRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: spacing.sm,
  },
  ruleAddText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  editPreviewWrap: {
    height: 120,
    borderRadius: radius.lg,
    overflow: 'hidden',
    backgroundColor: colors.faint,
    marginBottom: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  editAvatarPreview: {
    width: 96,
    height: 96,
    borderRadius: 48,
    overflow: 'hidden',
  },
  editPickBtn: {
    marginBottom: spacing.md,
  },
  editColorLabel: {
    ...typographyScale.caption,
    ...fonts.semibold,
    color: colors.ink2,
    marginBottom: spacing.sm,
  },
  editSwatchStrip: {
    paddingVertical: spacing.xs,
    paddingRight: spacing.md,
    gap: spacing.sm,
  },
  editSwatch: {
    width: 36,
    height: 36,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  editSwatchSelected: {
    borderWidth: 2,
    borderColor: colors.white,
  },
  editColorNote: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: spacing.sm,
  },
  editActions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  editActionBtn: {
    flex: 1,
  },
});
